import API from '../../services/api';
import reportUtils from '../report';
import { buildCmsImpressionsRequest, getCmsSelectedSites, getMobileCmsTemplate } from './cmsImpressions';

const optionId = (cmsId, field, name) => 'cms-' + Array.from(JSON.stringify([cmsId, field, name]))
  .map(char => char.codePointAt(0).toString(16)).join('-');

// Distinct query results are not paginated. Read every returned dataset and CMS source.
export const loadCmsFilterOptions = async (fieldName, context, selectedCampaigns = []) => {
  const { projectId, sources, startDate, endDate, devices } = context;
  const results = await Promise.all(sources.map(async source => {
    const cmsId = source.vendor.toLowerCase();
    const campaigns = fieldName === 'advertisers' ? [] : selectedCampaigns
      .filter(item => !item.cmsId || item.cmsId === cmsId).map(item => item.name);
    // A selection containing only other vendors must not expand this vendor to all campaigns.
    if (fieldName === 'medias' && selectedCampaigns.length && !campaigns.length) return [];
    let values;
    if (context.mobile) {
      if (cmsId !== 'broadsign' || !getCmsSelectedSites(context.selectedSites, cmsId).length) return [];
      const template = getMobileCmsTemplate(context.templates, source.vendor.toUpperCase());
      if (!template) throw new Error('Mobile CMS filters are not configured');
      const request = buildCmsImpressionsRequest({
        projectId, templateId: template.id || template.name.replace(/^templates\//, ''), cmsId,
        filters: { ...context, selectedAdvertisers: campaigns, selectedMedias: [] }
      });
      const dimension = fieldName === 'advertisers' ? 'campaign' : 'media';
      const response = await API.getAnalyticsViewData(`cms_impressions_by_${dimension}`, request);
      const points = response.categorySeries?.points || [];
      if (fieldName === 'advertisers') {
        return points.map(point => {
          if (!point.key) throw new Error('Missing campaign ID in CMS response');
          const name = point.values?.campaign || point.label || point.key;
          return { id: optionId(cmsId, fieldName, point.key), campaignId: point.key, cmsId, name,
            displayName: sources.length > 1 ? `${name} (${source.vendor})` : name };
        });
      }
      values = points.map(point => point.values?.[dimension]);
    } else {
      const field = source.popColumnMappings?.[fieldName];
      if (!field || !source.sourceTable) throw new Error('Missing CMS filter mapping');
      const response = await API.getCMSData(projectId, source.sourceTable,
        reportUtils.generateCMSQueryObject(field, startDate, endDate), campaigns, devices);
      if (response.error || response.code || response.status >= 400) throw new Error('CMS query failed');
      values = (response.datasets || []).flatMap(dataset => {
        const value = dataset.value;
        return Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
      });
    }
    return Array.from(new Set(values.filter(value => typeof value === 'string' && value.length)))
      .map(name => ({ id: optionId(cmsId, fieldName, name), cmsId, name,
        displayName: sources.length > 1 ? `${name} (${source.vendor})` : name }));
  }));
  return Array.from(new Map(results.flat().map(item => [item.id, item])).values())
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};
